import { useCallback } from "react";

function isProductRecommended(value) {
  return value === true || value === 1 || String(value || "").trim().toLowerCase() === "true";
}

export default function ProductCatalogActions({
  adminFetch,
  apiBaseUrl,
  readApiJson,
  setAdminProducts,
  editingProductId,
  resetProductEditorState,
  fetchAdminData,
  setAdminError
}) {
  const uploadAdminProductAssets = useCallback(async (files, options = {}) => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (!selectedFiles.length) {
      return [];
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("files", file);
    });
    formData.append("kind", String(options.kind || "misc"));

    const response = await adminFetch(`${apiBaseUrl}/admin/uploads/product-assets`, {
      method: "POST",
      body: formData
    });

    const data = await readApiJson(response);
    if (!response.ok) {
      throw new Error(data.message || "Upload failed");
    }

    return Array.isArray(data?.files) ? data.files.map((item) => item.url).filter(Boolean) : [];
  }, [adminFetch, apiBaseUrl, readApiJson]);

  const deleteAdminProductAssets = useCallback(async (urls) => {
    const uniqueUrls = Array.from(new Set((Array.isArray(urls) ? urls : []).filter(Boolean)));
    if (!uniqueUrls.length) {
      return;
    }

    const response = await adminFetch(`${apiBaseUrl}/admin/uploads/product-assets`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ urls: uniqueUrls })
    });

    const data = await readApiJson(response);
    if (!response.ok) {
      throw new Error(data.message || "Asset cleanup failed");
    }
  }, [adminFetch, apiBaseUrl, readApiJson]);

  const deleteProduct = useCallback(async (productId) => {
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/products/${productId}`, {
        method: "DELETE"
      });

      if (!response.ok) {
        throw new Error("Delete product failed");
      }

      setAdminProducts((prev) => prev.filter((item) => item.id !== productId));
      if (editingProductId === productId) {
        resetProductEditorState();
      }
      await fetchAdminData();
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message);
      }
    }
  }, [
    adminFetch,
    apiBaseUrl,
    editingProductId,
    fetchAdminData,
    resetProductEditorState,
    setAdminError,
    setAdminProducts
  ]);

  async function toggleProductRecommended(productId, isRecommended) {
    try {
      const nextRecommended = isProductRecommended(isRecommended);
      const response = await adminFetch(`${apiBaseUrl}/admin/products/${productId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          isRecommended: nextRecommended
        })
      });

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data?.message || "Update product recommendation failed");
      }

      const resolvedRecommended = isProductRecommended(data?.product?.isRecommended)
        ? true
        : (data?.product?.isRecommended !== undefined ? false : nextRecommended);

      setAdminProducts((prev) => prev.map((item) => (
        Number(item?.id) === Number(productId)
          ? { ...item, isRecommended: resolvedRecommended }
          : item
      )));
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message);
      }
    }
  }

  return {
    uploadAdminProductAssets,
    deleteAdminProductAssets,
    deleteProduct,
    toggleProductRecommended
  };
}
